import JSPDF from "jspdf";
import html2canvas from "html2canvas";

/**
 * Подготавливает содержимое для рендеринга, разворачивая таблицу.
 * @param content DOM-элемент с таблицей
 * @returns Объект с оригинальными стилями
 */
const prepareContent = (content: HTMLElement) => {
  const originalOverflow = content.style.overflow;
  const originalHeight = content.style.height;
  const originalWidth = content.style.width;
  const originalPaddingBottom = content.style.paddingBottom;

  content.style.paddingBottom = "0px";
  content.style.overflow = "visible";
  content.style.height = `${content.scrollHeight}px`;
  content.style.width = `${content.scrollWidth}px`;

  return {
    originalOverflow,
    originalHeight,
    originalWidth,
    originalPaddingBottom,
  };
};

/**
 * Восстанавливает оригинальные стили содержимого.
 * @param content DOM-элемент с таблицей
 * @param originals Объект с оригинальными стилями
 */
const restoreContent = (
  content: HTMLElement,
  originals: {
    originalOverflow: string;
    originalHeight: string;
    originalWidth: string;
    originalPaddingBottom: string;
  }
) => {
  content.style.paddingBottom = originals.originalPaddingBottom;
  content.style.overflow = originals.originalOverflow;
  content.style.height = originals.originalHeight;
  content.style.width = originals.originalWidth;
};

/**
 * Рендер содержимого в canvas.
 * @param content DOM-элемент
 * @param scale Масштаб
 * @returns Canvas элемент
 */
const renderCanvas = async (content: HTMLElement, scale: number) => {
  return await html2canvas(content, {
    scale,
    useCORS: true,
  });
};

/**
 * Вычисляет позиции нижних границ строк в пикселях canvas.
 * @param content DOM-элемент
 * @param rows Массив строк
 * @param scale Масштаб
 * @returns Массив позиций
 */
const getRowBottoms = (
  content: HTMLElement,
  rows: Element[],
  scale: number
) => {
  const contentRect = content.getBoundingClientRect();
  const rowPositions = rows.map((row) => {
    const rect = (row as HTMLElement).getBoundingClientRect();
    return {
      top: rect.top - contentRect.top,
      height: rect.height,
    };
  });
  return rowPositions.map((pos) => (pos.top + pos.height) * scale);
};

/**
 * Создаёт canvas для заголовка.
 * @param canvas Основной canvas
 * @param headerHeightPx Высота заголовка в px
 * @returns Canvas заголовка
 */
const createHeaderCanvas = (
  canvas: HTMLCanvasElement,
  headerHeightPx: number
) => {
  const headerCanvas = document.createElement("canvas");
  headerCanvas.width = canvas.width;
  headerCanvas.height = headerHeightPx;
  const headerCtx = headerCanvas.getContext("2d");
  headerCtx?.drawImage(
    canvas,
    0,
    0,
    canvas.width,
    headerHeightPx,
    0,
    0,
    canvas.width,
    headerHeightPx
  );
  return headerCanvas;
};

/**
 * Вычисляет точки разреза для тела таблицы.
 * @param bodyRowBottoms Массив позиций строк тела
 * @param bodyStartY Начало тела
 * @param pageHeightPx Высота страницы в px
 * @param headerHeightPx Высота заголовка в px
 * @returns Массив точек разреза
 */
const calculateBodyCuts = (
  bodyRowBottoms: number[],
  bodyStartY: number,
  pageHeightPx: number,
  headerHeightPx: number
) => {
  const bodyCuts: number[] = [bodyStartY];
  let currentBodyY = bodyStartY;
  let bodyRowIdx = 0;
  const bodyAvailPxFirst = pageHeightPx - headerHeightPx;
  let isFirstPage = true;

  while (bodyRowIdx < bodyRowBottoms.length) {
    const bodyAvailPx = isFirstPage ? bodyAvailPxFirst : pageHeightPx;
    const pageBodyBottom = currentBodyY + bodyAvailPx;

    let lastFit = -1;
    for (let j = bodyRowIdx; j < bodyRowBottoms.length; j++) {
      if (bodyRowBottoms[j] > pageBodyBottom) {
        break;
      }
      lastFit = j;
    }

    if (lastFit < bodyRowIdx) {
      lastFit = bodyRowIdx;
    }

    const cutY = bodyRowBottoms[lastFit];
    bodyCuts.push(cutY);

    currentBodyY = cutY;
    bodyRowIdx = lastFit + 1;
    isFirstPage = false;
  }

  return bodyCuts;
};

/**
 * Добавляет срез на страницу PDF.
 * @param pdf Объект PDF
 * @param canvas Основной canvas
 * @param y Начальная позиция Y
 * @param sliceHeight Высота среза
 * @param marginMm Отступ
 * @param usableWidth Полезная ширина
 * @param pxPerMm Пикселей на мм
 * @param yPosMm Позиция Y в мм
 */
const addPageSlice = (
  pdf: JSPDF,
  canvas: HTMLCanvasElement,
  y: number,
  sliceHeight: number,
  marginMm: number,
  usableWidth: number,
  pxPerMm: number,
  yPosMm: number
) => {
  const pageCanvas = document.createElement("canvas");
  pageCanvas.width = canvas.width;
  pageCanvas.height = sliceHeight;
  const ctx = pageCanvas.getContext("2d");
  ctx?.drawImage(
    canvas,
    0,
    y,
    canvas.width,
    sliceHeight,
    0,
    0,
    canvas.width,
    sliceHeight
  );

  const imgData = pageCanvas.toDataURL("image/jpeg", 1);

  pdf.addImage(
    imgData,
    "JPEG",
    marginMm,
    yPosMm,
    usableWidth,
    sliceHeight / pxPerMm,
    undefined,
    "SLOW"
  );
};

/**
 * Создаёт PDF-файл из DOM-элемента таблицы с графиком смен и отправляет на печать.
 *
 * @param content DOM-элемент с таблицей
 */
export const generatePDF = async (content: HTMLElement) => {
  if (!content) return false;

  const originals = prepareContent(content);

  try {
    const marginMm = 10;
    const scale = 4; // Увеличен с 2 для повышения четкости в файле

    const pdf = new JSPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const usableWidth = pageWidth - marginMm * 2;
    const usableHeight = pageHeight - marginMm * 2;

    const canvas = await renderCanvas(content, scale);

    const pxPerMm = canvas.width / usableWidth;
    const pageHeightPx = usableHeight * pxPerMm;

    const rows = Array.from(content.querySelectorAll("tr"));
    const rowBottoms = getRowBottoms(content, rows, scale);

    const headerHeightPx = rowBottoms[0];
    const bodyStartY = headerHeightPx;

    createHeaderCanvas(canvas, headerHeightPx);

    const bodyRowBottoms = rowBottoms.slice(1);
    const bodyCuts = calculateBodyCuts(
      bodyRowBottoms,
      bodyStartY,
      pageHeightPx,
      headerHeightPx
    );

    const numPages = bodyCuts.length - 1;

    for (let p = 0; p < numPages; p++) {
      if (p > 0) pdf.addPage();

      let y: number;
      let sliceHeight: number;

      if (p === 0) {
        y = 0;
        sliceHeight = bodyCuts[1];
      } else {
        y = bodyCuts[p];
        sliceHeight = bodyCuts[p + 1] - bodyCuts[p];
      }

      let yPosMm = marginMm;
      if (numPages === 1) {
        const contentHeightMm = sliceHeight / pxPerMm;
        yPosMm = marginMm + (usableHeight - contentHeightMm) / 2;
      }

      addPageSlice(
        pdf,
        canvas,
        y,
        sliceHeight,
        marginMm,
        usableWidth,
        pxPerMm,
        yPosMm
      );
    }

    pdf.autoPrint();
    window.open(pdf.output("bloburl"), "_blank");
  } catch (error) {
    console.error("Ошибка при генерации PDF:", error);
  } finally {
    restoreContent(content, originals);
  }
};
